<template>
  <div class="mobile-page">
    <div class="mobile-header list-header">
      <h2><el-icon><Checked /></el-icon> 巡检记录</h2>
      <p>共 {{ total }} 条记录 · 现场逐项检查留痕</p>
    </div>

    <div class="mobile-body">
      <!-- 搜索 + 日期 -->
      <div class="search-line">
        <el-input v-model="kw" placeholder="搜索巡检点 / 巡检人 / 区域 / 计划" clearable size="default" @keyup.enter="onSearch" @clear="onSearch">
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-button size="default" circle @click="dateVisible = true"><el-icon><Filter /></el-icon></el-button>
        <el-button size="default" circle @click="reload"><el-icon><Refresh /></el-icon></el-button>
      </div>

      <p v-if="dateRangeText" class="active-filters">
        <el-tag size="small" closable @close="clearDate">{{ dateRangeText }}</el-tag>
      </p>

      <!-- 时间段：默认仅当天，其他时段手动选择 -->
      <div class="chip-row">
        <span v-for="r in ranges" :key="r.key"
          class="chip" :class="{ on: quickSel === r.key }"
          @click="setQuick(r.key)"
        >{{ r.label }}</span>
        <span class="chip" :class="{ on: quickSel === 'custom' }" @click="dateVisible = true">
          <el-icon :size="12"><Filter /></el-icon> 自定义
        </span>
      </div>
      <p class="data-tip">默认仅显示当天的巡检记录，其他时段请手动选择。</p>

      <!-- 计划类型筛选（来源于总览统计，保证轻量） -->
      <div v-if="typeChips.length" class="chip-row">
        <span class="chip" :class="{ on: typeSel === '' }" @click="typeSel = ''; onSearch()">全部类型</span>
        <span
          v-for="t in typeChips" :key="t"
          class="chip" :class="{ on: typeSel === t }"
          @click="typeSel = t; onSearch()"
        >{{ t }}</span>
      </div>

      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="error" class="white-card center-box">
        <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
        <p class="sub-text">{{ error }}</p>
        <el-button size="small" type="primary" plain style="margin-top:10px" @click="reload">重试</el-button>
      </div>
      <div v-else-if="records.length === 0" class="white-card center-box">
        <el-icon :size="42" color="#c0c4cc"><Checked /></el-icon>
        <p class="sub-text">暂无巡检记录</p>
      </div>

      <template v-else>
        <div class="rec-card" v-for="r in records" :key="r.id" @click="open(r)">
          <div class="rc-head">
            <span class="rc-code mono">{{ r.pointCode }}</span>
            <span class="pill pill-blue">{{ r.planType || '日常' }}</span>
          </div>
          <div class="rc-name">{{ r.pointName || '未命名巡检点' }}<span v-if="r.area" class="rc-area">{{ r.area }}</span></div>
          <div class="rc-badges">
            <span v-if="r.hazardCount" class="pill pill-red">异常 {{ r.hazardCount }}</span>
            <span v-if="r.photoCount" class="pill pill-gray">照片 {{ r.photoCount }}</span>
            <span class="pill pill-gray">检查 {{ r.itemCount }} 项</span>
          </div>
          <div class="rc-meta">
            <span>👤 {{ r.inspector || '未知' }}<template v-if="r.roleType">（{{ r.roleType }}）</template></span>
            <span class="rc-time">{{ fmt(r.checkTime) }}</span>
          </div>
          <div class="rc-plan" v-if="r.planName">计划：{{ r.planName }}</div>
        </div>

        <div v-if="records.length < total" class="load-more">
          <el-button v-if="!loadingMore" text type="primary" @click="loadMore">加载更多（{{ records.length }}/{{ total }}）</el-button>
          <span v-else class="sub-text"><el-icon class="is-loading"><Loading /></el-icon> 加载中…</span>
        </div>
        <p v-else-if="records.length" class="no-more">— 已经到底了 —</p>
      </template>
    </div>

    <!-- 日期筛选抽屉 -->
    <el-drawer v-model="dateVisible" title="按日期筛选" size="72%" direction="btt">
      <div class="drawer-body">
        <el-form label-position="top">
          <el-form-item label="开始日期">
            <el-date-picker v-model="startDate" type="date" placeholder="开始" value-format="YYYY-MM-DD" style="width:100%" />
          </el-form-item>
          <el-form-item label="结束日期">
            <el-date-picker v-model="endDate" type="date" placeholder="结束" value-format="YYYY-MM-DD" style="width:100%" />
          </el-form-item>
        </el-form>
        <div class="drawer-actions">
          <el-button @click="clearDate" style="width:30%">重置</el-button>
          <el-button type="primary" @click="applyDate" style="width:65%">确定</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchInspectRecordsSlim, fetchOverview } from '../api'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()

const records = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = 30
const loading = ref(true)
const loadingMore = ref(false)
const error = ref('')
const kw = ref('')
const typeSel = ref('')
const typeChips = ref([])
const startDate = ref('')
const endDate = ref('')
const dateVisible = ref(false)
const quickSel = ref('today')

const ranges = [
  { key: 'today', label: '今天' },
  { key: 'week', label: '近7天' },
  { key: 'month', label: '本月' },
  { key: 'all', label: '全部' }
]

function todayStr() { return dayjs().format('YYYY-MM-DD') }

// 默认：仅当天（如需其他时段手动选择）
if (!route.query.startDate && !route.query.endDate) {
  startDate.value = todayStr()
  endDate.value = todayStr()
} else {
  quickSel.value = 'custom'
}

function setQuick(key) {
  quickSel.value = key
  const today = todayStr()
  if (key === 'today') {
    startDate.value = today
    endDate.value = today
  } else if (key === 'week') {
    startDate.value = dayjs().subtract(6, 'day').format('YYYY-MM-DD')
    endDate.value = today
  } else if (key === 'month') {
    startDate.value = dayjs().startOf('month').format('YYYY-MM-DD')
    endDate.value = today
  } else {
    startDate.value = ''
    endDate.value = ''
  }
  load(true)
}

const dateRangeText = computed(() => {
  if (startDate.value && endDate.value) {
    if (startDate.value === endDate.value) return startDate.value + '（当天）'
    return `${startDate.value} ~ ${endDate.value}`
  }
  if (startDate.value) return `${startDate.value} 起`
  if (endDate.value) return `至 ${endDate.value}`
  return ''
})

function fmt(t) {
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : ''
}

function buildParams() {
  const params = { page: page.value, pageSize }
  if (kw.value.trim()) params.kw = kw.value.trim()
  if (typeSel.value) params.planType = typeSel.value
  if (startDate.value) params.startDate = startDate.value
  if (endDate.value) params.endDate = endDate.value
  return params
}

async function fetchPage() {
  const r = await fetchInspectRecordsSlim(buildParams())
  return r
}

async function load(reset = true) {
  if (reset) {
    page.value = 1
    loading.value = true
  } else {
    loadingMore.value = true
  }
  try {
    const r = await fetchPage()
    if (reset) {
      records.value = r.data || []
      total.value = r.total || 0
    } else {
      records.value = records.value.concat(r.data || [])
    }
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

function reload() { load(true) }
function onSearch() { load(true) }
function loadMore() {
  page.value += 1
  load(false)
}

function applyDate() {
  quickSel.value = 'custom'
  dateVisible.value = false
  load(true)
}
function clearDate() {
  startDate.value = ''
  endDate.value = ''
  quickSel.value = 'all'
  dateVisible.value = false
  load(true)
}

function open(r) {
  router.push({ path: `/record-inspect/${r.id}` })
}

async function loadTypes() {
  try {
    const ov = await fetchOverview()
    const byType = (ov.data && ov.data.byType) || []
    typeChips.value = byType.map(x => x.t).slice(0, 8)
  } catch { /* 类型统计不可用不影响列表 */ }
}

onMounted(() => {
  load(true)
  loadTypes()
})
</script>

<style scoped>
.search-line { display: flex; gap: 8px; margin-bottom: 8px; }
.search-line .el-input { flex: 1; }
.active-filters { margin-bottom: 8px; }
.data-tip { font-size: 11px; color: #b0b3bb; margin: -4px 2px 8px; }

.rec-card {
  background: #fff; border-radius: 14px; padding: 13px 14px; margin-bottom: 10px;
  box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; transition: transform .15s;
}
.rec-card:active { transform: scale(.99); }
.rc-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
.rc-code { font-size: 13px; font-weight: 700; color: #1a73e8; }
.rc-name { font-size: 15px; font-weight: 700; color: #202124; }
.rc-area { font-size: 12px; color: #9aa0a6; font-weight: 400; margin-left: 6px; }
.rc-badges { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.rc-meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #5f6368; margin-top: 8px; }
.rc-time { margin-left: auto; }
.rc-plan { font-size: 12px; color: #9aa0a6; margin-top: 4px; }

.load-more { text-align: center; padding: 6px 0; }
.no-more { text-align: center; color: #c0c4cc; font-size: 12px; padding: 12px 0; }

.drawer-body { padding: 4px; }
.drawer-actions { display: flex; gap: 10px; margin-top: 16px; }
</style>
